import type { ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon, type IconName } from '@/components/Icon'
import '@/styles/record.css'

/**
 * «Следующий шаг» (CompRecord.dc.html) — первым на обзоре карточки.
 *
 * Два вида, и они нарочно разные: ближайшая задача — спокойная акцентная плашка;
 * **отсутствие** задачи — предупреждение с иконкой и словом. Пустое место на месте шага
 * читалось бы как «всё сделано», а это ровно обратное: запись, по которой никто ничего не
 * собирается делать, — главная находка карточки.
 */
export interface NextStepTask {
  title: ReactNode
  /** Срок, исполнитель — одной строкой. */
  details?: ReactNode
  /** Срок прошёл: флаг словом рядом со сроком, а не только цвет. */
  overdue?: boolean
  icon?: IconName
}

export interface NextStepProps {
  /** `null` — шага нет, показывается предупреждение. */
  task: NextStepTask | null
  /** Действия над шагом («Выполнено»); без права — не передаются. */
  actions?: ReactNode
  /** Почему отсутствие шага плохо — для этой сущности. */
  missingHint?: ReactNode
  /** Действие «Назначить шаг»; без права — не передаётся. */
  missingAction?: ReactNode
}

export function NextStep({ task, actions, missingHint, missingAction }: NextStepProps) {
  const { t } = useApp()
  if (!task) {
    return (
      <section className="next-step is-missing" role="status" aria-label={t('record.next.missing')}>
        <span className="ico">
          <Icon name="flag" />
        </span>
        <div className="stack" style={{ gap: 2 }}>
          <span className="strong" style={{ color: 'var(--warning-text)' }}>
            {t('record.next.missing')}
          </span>
          {missingHint && <span className="t-sm secondary">{missingHint}</span>}
        </div>
        <div className="row">{missingAction}</div>
      </section>
    )
  }
  return (
    <section className="next-step" aria-label={t('record.next.title')}>
      <span className="ico">
        <Icon name={task.icon ?? 'work'} />
      </span>
      <div className="stack" style={{ gap: 2 }}>
        <span className="overline" style={{ color: 'var(--accent-text)' }}>
          {t('record.next.title')}
        </span>
        <span className="strong">{task.title}</span>
        {(task.details || task.overdue) && (
          <span className="t-sm secondary row-wrap" style={{ gap: 6 }}>
            {task.overdue && (
              <span className="flag flag-danger">
                <Icon name="clock" />
                {t('record.next.overdue')}
              </span>
            )}
            {task.details}
          </span>
        )}
      </div>
      <div className="row">{actions}</div>
    </section>
  )
}
